import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Zap } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const HERO_IMG =
  "https://images.unsplash.com/photo-1721815693498-cc28507c0ba2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3VzZSUyMGludGVyaW9yJTIwZGVzaWduJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc5MTg0NTIwNHww&ixlib=rb-4.1.0&q=85";

const LINES = ["ONE CALL.", "EVERY PROPERTY", "NEED."];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.35 + i * 0.14 },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const starY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden pt-36 pb-20 sm:pt-44 lg:pt-52" data-testid="hero-section">
      <motion.div
        style={{ y: starY }}
        className="pointer-events-none absolute -right-40 -top-24 opacity-[0.07] animate-spin-slow"
        aria-hidden="true"
      >
        <svg width="640" height="640" viewBox="0 0 100 100" fill="none">
          <path
            d="M50 5L61 36L94 39L68 60L76 93L50 74L24 93L32 60L6 39L39 36L50 5Z"
            fill="#FF2A3B"
          />
        </svg>
      </motion.div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="font-jbmono text-xs sm:text-sm uppercase tracking-[0.25em] text-[#FF2A3B] font-semibold"
          data-testid="hero-eyebrow"
        >
          ✦ Red Star Solutions • Property Maintenance
        </motion.p>

        <h1 className="mt-6 font-display font-extrabold tracking-tight leading-[1.05] text-[clamp(1.1rem,5.8vw,4.5rem)]" data-testid="hero-headline">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
              >
                {i === 2 ? (
                  <span className="text-[#FF2A3B] text-glow-red">NEED.</span>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="mt-7 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed"
          data-testid="hero-subheadline"
        >
          Electrical, plumbing, HVAC, carpentry, painting, cleaning, janitorial
          and handyman work for homes and commercial properties — one call,
          every trade covered.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.05 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollToId("#booking")}
            className="group flex items-center gap-2 rounded-full bg-[#FF2A3B] px-7 py-4 text-sm font-semibold text-white transition-all hover:bg-[#E01F30] hover:shadow-[0_0_40px_rgba(255,42,59,0.45)]"
            data-testid="hero-book-visit-button"
          >
            Book Maintenance Visit
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </button>
          <button
            onClick={() => scrollToId("#booking")}
            className="flex items-center gap-2 rounded-full border border-white/15 px-7 py-4 text-sm font-semibold text-slate-200 transition-all hover:border-[#FF2A3B]/60 hover:bg-white/5"
            data-testid="hero-urgent-request-button"
          >
            <Zap size={16} className="text-[#FFB800]" />
            Submit an Urgent Service Request
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="relative mt-16"
        >
          <motion.div style={{ y: imgY }} className="relative overflow-hidden rounded-2xl border border-white/10">
            <img
              src={HERO_IMG}
              alt="Architectural interior maintained by Red Star Solutions"
              className="h-[320px] sm:h-[440px] w-full object-cover"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0E] via-transparent to-transparent" />
            <div className="absolute bottom-5 left-5 flex items-center gap-3 rounded-full border border-white/10 bg-[#0A0B0E]/70 px-5 py-3 backdrop-blur-md">
              <span className="h-2 w-2 rounded-full bg-[#10B981] animate-pulse" />
              <span className="font-jbmono text-xs uppercase tracking-[0.18em] text-slate-200" data-testid="hero-status-badge">
                Now booking • Greater Cincinnati & the Tri-State
              </span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
